// CPF Clarity Explorer content: topics (official-source summaries) and source links.

export const TOPICS = [
  {
    tabTitle: "What happens to my CPF at 55?",
    tabSub: "Accounts & retirement sums",
    kicker: "AGE 55",
    title: "What happens to my CPF at 55?",
    description:
      "On your 55th birthday, a Retirement Account (RA) is created. Savings from your Special Account (SA), followed by your Ordinary Account (OA), are transferred to the RA up to your Full Retirement Sum (FRS). Your SA is then closed and any remaining SA savings move to your OA.",
    points: [
      "RA savings earn the same long-term interest rate as the SA and are used to provide your monthly payouts in retirement",
      "Savings above the FRS remain in the OA and are withdrawable",
      "From age 55, the maximum top-up you can receive in your RA rises to the Enhanced Retirement Sum (ERS), for higher payouts",
    ],
    myth: "\u201CI can withdraw all my CPF savings when I turn 55.\u201D",
    verdict: "MYTH",
    answer:
      "After setting aside your FRS, excess OA savings are withdrawable. If you are unable to set aside the FRS, you may still be able to withdraw up to $5,000 from age 55. Note that your CPF contribution rates also decrease at 55, and contributions go to your OA, MediSave and RA (or to your OA once your FRS is met).",
    sourceLabel: "Reaching age 55",
    sourceUrl: "https://www.cpf.gov.sg/member/retirement-income/milestones/reaching-age-55",
  },
  {
    tabTitle: "How much may I withdraw?",
    tabSub: "Options & conditions",
    kicker: "WITHDRAWALS",
    title: "How much may I withdraw?",
    description:
      "After setting aside your FRS in the RA, excess OA savings can be withdrawn. If you cannot set aside your FRS, up to $5,000 may be kept in your OA and withdrawn from age 55.",
    points: [
      "Your actual amount depends on your balances and applicable conditions",
      "If you own a property, you can choose to set aside up to half of the FRS using your property and withdraw RA savings above the Basic Retirement Sum (BRS). When you later sell the property, the amount withdrawn plus accrued interest must be refunded to restore your RA up to your FRS",
      "If you were born in 1957 or after, you can withdraw an additional amount from your RA from age 65. The amount depends on your birth year and excludes cash top-ups, CPF transfers and government grants",
    ],
    myth: "\u201CEveryone can withdraw the same amount at age 55.\u201D",
    verdict: "MYTH",
    answer:
      "Individual outcomes differ. Your CPF balances, the amount set aside in your RA, whether you own a property and your birth year all affect what is available for withdrawal. Withdrawn savings can also be used later to increase your monthly payouts instead.",
    sourceLabel: "Retirement withdrawals at 55",
    sourceUrl: "https://www.cpf.gov.sg/member/retirement-income/milestones/reaching-age-55",
  },
  {
    tabTitle: "What are BRS, FRS and ERS?",
    tabSub: "Reference points explained",
    kicker: "RETIREMENT SUMS",
    title: "What are BRS, FRS and ERS?",
    description:
      "The CPF Retirement Sum is the principal amount of savings in your Retirement Account (RA). The more savings you have in your RA, the higher your monthly payouts in retirement. There are three reference points to guide how much to set aside.",
    points: [
      "Basic Retirement Sum (BRS): provides monthly payouts for basic living needs, excluding rental expenses. If you own a Singapore property with a lease lasting until you are at least 95, you may apply to withdraw RA savings down to your BRS",
      "Full Retirement Sum (FRS): double your BRS, and the default amount set aside in your RA when you turn 55",
      "Enhanced Retirement Sum (ERS): double the current year\u2019s FRS, and the maximum you can top up your RA to from age 55. The ERS rises yearly; topping up to the current year\u2019s ERS gives the highest CPF monthly payouts",
    ],
    myth: "\u201CThe highest retirement sum is automatically right for everyone.\u201D",
    verdict: "MYTH",
    answer:
      "The retirement sums are guideposts, not targets everyone must hit. More RA savings generally mean higher monthly payouts, but the right level depends on your income needs, property situation and other savings. If you are 55 or above, the CPF Retirement Dashboard shows whether you have set aside your FRS and how much more you can top up.",
    sourceLabel: "What are the retirement sums (BRS, FRS and ERS)?",
    sourceUrl:
      "https://www.cpf.gov.sg/service/article/what-are-the-retirement-sums-basic-retirement-sum-brs-full-retirement-sum-frs-and-enhanced-retirement-sum-ers",
  },
  {
    tabTitle: "When should my monthly payouts start?",
    tabSub: "Start now or defer",
    kicker: "AGES 65 TO 70",
    title: "When should my monthly payouts start?",
    description:
      "You can choose to start your monthly payouts anytime from age 65 to 70, and can submit your decision as early as three months before turning 65. Deferring allows you to receive higher payouts later; payouts start automatically at 70 if you have not started earlier.",
    points: [
      "CPF LIFE payouts increase by up to 7% for each year deferred, so deferring to age 70 can increase them by up to 35%",
      "If you are not on CPF LIFE, deferring means either higher payouts or a longer payout duration",
      "If you were born in 1958 or after and have not met your FRS when payouts start, OA savings are automatically transferred to your RA up to the FRS to give you higher payouts",
    ],
    myth: "\u201CMy CPF monthly payouts must start as soon as I turn 65.\u201D",
    verdict: "MYTH",
    answer:
      "Age 65 is the payout eligibility age, but you can choose to start anytime up to age 70. If you have not started earlier, payouts begin automatically at 70. If you are on CPF LIFE and have not chosen a plan by then, you will be placed on the Standard Plan.",
    sourceLabel: "Reaching age 65",
    sourceUrl: "https://www.cpf.gov.sg/member/retirement-income/milestones/reaching-age-65",
  },
  {
    tabTitle: "Which CPF LIFE plan should I understand?",
    tabSub: "Payout patterns compared",
    kicker: "CPF LIFE",
    title: "Which CPF LIFE plan should I understand?",
    description:
      "CPF LIFE is a national longevity insurance annuity scheme that provides monthly payouts no matter how long you live. You are automatically included if you are a Singapore Citizen or PR, born in 1958 or after, and have at least $60,000 in retirement savings when your payouts start. There are three plans with different payout patterns.",
    points: [
      "Escalating Plan: payouts start lower but grow by 2% a year for life, helping you keep pace with rising prices",
      "Standard Plan: steady, level monthly payouts (the default if no plan is chosen by age 70)",
      "Basic Plan: payouts start low and fall when your CPF balances drop below $60,000",
    ],
    myth: "\u201CAll CPF LIFE plans provide the same payout pattern.\u201D",
    verdict: "MYTH",
    answer:
      "Escalating provides growing payouts, Standard provides steady payouts and Basic provides progressively lower payouts. All three pay for life, and when you pass away any remaining CPF LIFE premium balance and CPF savings go to your loved ones. Your CPF LIFE savings are guaranteed by the Singapore Government.",
    sourceLabel: "Compare CPF LIFE plans",
    sourceUrl: "https://www.cpf.gov.sg/member/retirement-income/monthly-payouts/cpf-life",
  },
  {
    tabTitle: "What if I\u2019m not on CPF LIFE?",
    tabSub: "Payout duration & options",
    kicker: "OTHER MONTHLY PAYOUTS",
    title: "What if I\u2019m not on CPF LIFE?",
    description:
      "You are not automatically on CPF LIFE if you were born before 1958, have less than $60,000 in retirement savings when payouts start, or are not a Singapore Citizen or PR. Your monthly payouts are then paid from your Retirement Account and stop when those savings run out. You can apply to start them from age 65; they start automatically at 70.",
    points: [
      "Payouts are calculated to last up to 20 years from your payout eligibility age, based on the prevailing RA interest rate when you start",
      "Extra interest of up to 2% a year on your retirement savings is used to extend your payouts, up to age 90",
      "Deferring, cash top-ups and CPF transfers give you higher payouts or a longer payout duration",
    ],
    myth: "\u201CIf I\u2019m not automatically included in CPF LIFE, I can never join.\u201D",
    verdict: "MYTH",
    answer:
      "A Singapore Citizen or Permanent Resident who is not automatically included can still choose to join CPF LIFE any time from age 65 to one month before turning 80, to receive payouts for life. As more than 1 in 2 Singaporeans aged 65 today can expect to live beyond 85, CPF Board encourages members to consider it.",
    sourceLabel: "Monthly payouts if not on CPF LIFE",
    sourceUrl: "https://www.cpf.gov.sg/member/retirement-income/monthly-payouts/non-cpf-life",
  },
];

export const SOURCE_LINKS = [
  { label: "Retirement income \u2197", url: "https://www.cpf.gov.sg/member/retirement-income" },
  { label: "Reaching age 55 \u2197", url: "https://www.cpf.gov.sg/member/retirement-income/milestones/reaching-age-55" },
  { label: "Reaching age 65 \u2197", url: "https://www.cpf.gov.sg/member/retirement-income/milestones/reaching-age-65" },
  { label: "CPF LIFE \u2197", url: "https://www.cpf.gov.sg/member/retirement-income/monthly-payouts/cpf-life" },
  { label: "Other payouts \u2197", url: "https://www.cpf.gov.sg/member/retirement-income/monthly-payouts/non-cpf-life" },
];
